import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useCandidateQuery } from "@/queries/candidate/candidate";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";

import {
  candidateQuestionnaireFormSchema,
  CandidateQuestionnaireFormSchema,
} from "./schema";
import {
  businessPrincipleList,
  communicationSkillList,
  peerDescriptionList,
} from "@/redux/features/candidateProfileSlice";

import { useUpdateCandidateProfile } from "@/mutations/candidate/profile/updateProfile";

const CandidateQuestionnaireEditFormDialog = ({
  profileId,
}: {
  profileId: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { candidate } = useCandidateQuery(profileId);
  const { updateCandidateProfile, isUpdatingCandidateProfile } =
    useUpdateCandidateProfile(profileId, () => setIsOpen(false));

  const form = useForm<CandidateQuestionnaireFormSchema>({
    resolver: zodResolver(candidateQuestionnaireFormSchema),
    mode: "onChange",
    defaultValues: {
      questionnaireBusinessPrinciples:
        candidate?.questionnaireBusinessPrinciples ?? [
          ...businessPrincipleList,
        ],
      questionnairePeerDescription: candidate?.questionnairePeerDescription ?? [
        ...peerDescriptionList,
      ],
      questionnaireCommunicationSkills:
        candidate?.questionnaireCommunicationSkills ?? [
          ...communicationSkillList,
        ],
    },
  });

  useEffect(() => {
    form.reset({
      questionnaireBusinessPrinciples:
        candidate?.questionnaireBusinessPrinciples ?? [
          ...businessPrincipleList,
        ],
      questionnairePeerDescription: candidate?.questionnairePeerDescription ?? [
        ...peerDescriptionList,
      ],
      questionnaireCommunicationSkills:
        candidate?.questionnaireCommunicationSkills ?? [
          ...communicationSkillList,
        ],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, candidate]);

  const onSubmit = (data: CandidateQuestionnaireFormSchema) => {
    updateCandidateProfile({
      emails: candidate?.emails ?? [],
      ...data,
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="link" className="edit-text self-end">
          Edit
        </Button>
      </DialogTrigger>
      <DialogContent className="w-full sm:max-w-2xl">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <DialogTitle>Edit Questionnaire</DialogTitle>
          </div>
          <DialogDescription className="sr-only">
            Update your questionnaire below.
          </DialogDescription>
        </DialogHeader>
        <ScrollArea className="sm:max-h-[calc(100vh-10rem)] md:max-h-[70vh]">
          <Form {...form}>
            <form
              className="space-y-4 w-full"
              onSubmit={(e) => {
                e.preventDefault();
                void form.handleSubmit((data) => onSubmit(data))();
              }}
            >
              <FormField
                control={form.control}
                name="questionnaireBusinessPrinciples"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel hoverContent="Drag to re-order">
                      Rank the top business principles that drive you
                    </FormLabel>
                    <FormControl>
                      <Sorting values={field.value} onChange={field.onChange} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="questionnairePeerDescription"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel hoverContent="Drag to re-order">
                      How would your peers describe you?
                    </FormLabel>
                    <FormControl>
                      <Sorting values={field.value} onChange={field.onChange} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="questionnaireCommunicationSkills"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel hoverContent="Drag to re-order">
                      Stack rank from 1-3, 1 being your best skill to 3 your
                      least
                    </FormLabel>
                    <FormControl>
                      <Sorting values={field.value} onChange={field.onChange} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="w-full flex justify-between">
                <Button
                  variant="outline"
                  className="w-fit"
                  type="button"
                  onClick={() => setIsOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="secondary"
                  className="w-fit"
                  type="submit"
                  disabled={
                    !form.formState.isValid || isUpdatingCandidateProfile
                  }
                >
                  Save
                </Button>
              </div>
            </form>
          </Form>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

const Sorting = ({
  values,
  onChange,
}: {
  values?: string[];
  onChange: (v: string[]) => void;
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (
    event: DragEndEvent,
    list: string[] | undefined,
    onChange: (v: string[]) => void,
  ): void => {
    if (!list) {
      return;
    }

    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = list.indexOf(active.id as string);
      const newIndex = list.indexOf(over.id as string);

      onChange(arrayMove(list, oldIndex, newIndex));
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={(event) => handleDragEnd(event, values, onChange)}
    >
      <SortableContext
        items={values ?? []}
        strategy={verticalListSortingStrategy}
      >
        {values?.map((id, idx) => <SortableItem key={id} id={id} idx={idx} />)}
      </SortableContext>
    </DndContext>
  );
};

const SortableItem = (props: { key: string; id: string; idx: number }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: props.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="flex items-center"
    >
      <p>
        {props.idx + 1}. {props.id}
      </p>
    </div>
  );
};

export default CandidateQuestionnaireEditFormDialog;
